import {gql} from '@apollo/client';

import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ParameterStructuredTableRowMetadata} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {ParameterStructuredTableColumnUuid} from 'notebook/integrations/procedures/ParameterStructuredTableColumnInfo';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {getUpdatedCellWithLSUV} from 'notebook/structured-tables/row-updaters/getUpdatedCellWithLSUV';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';

import type {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {LiveCellValue} from '../LiveCellValues';
import {
  BulkParameterVersionLiveDataQuery_procedureDataParameterVersions_ProcedureDataParameterVersionNode as ParameterVersionLiveData,
  BulkParameterVersionLiveDataQuery,
  BulkParameterVersionLiveDataQueryVariables,
} from './__generated/BulkParameterVersionLiveDataQuery';

const PARAMETER_VERSION_LIVE_DATA = gql`
  fragment ParameterVersionLiveDataFragment on ProcedureDataParameterVersionNode {
    id
    name
    description
  }
`;

const PARAMETER_VERSION_LIVE_DATA_FRAGMENT_WRAPPER: FragmentWrapper<ParameterVersionLiveData> = {
  fragment: PARAMETER_VERSION_LIVE_DATA,
  fragmentName: 'ParameterVersionLiveDataFragment',
};

// Agent keep in sync: the server-side resync fetch fetch_parameter_version_live_data in
// benchling/notebook/structured_tables/resync/parameter_live_data/parameter_live_data.py, which runs the
// same procedureDataParameterVersions query so backend resync matches this path.
export const BULK_PARAMETER_VERSION_LIVE_DATA_QUERY = gql`
  query BulkParameterVersionLiveDataQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: PROCEDURES) {
    procedureDataParameterVersions(ids: $ids) {
      ...ParameterVersionLiveDataFragment
      ...InaccessibleInfoGraphQL
    }
  }
  ${PARAMETER_VERSION_LIVE_DATA}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<BulkParameterVersionLiveDataQuery['procedureDataParameterVersions']> {
  if (sampleLookupInfos.length === 0) {
    return [];
  }

  const {procedureDataParameterVersions} = await doQuerySafe<
    BulkParameterVersionLiveDataQuery,
    BulkParameterVersionLiveDataQueryVariables
  >(
    {query: BULK_PARAMETER_VERSION_LIVE_DATA_QUERY},
    {
      variables: {
        ids: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return procedureDataParameterVersions;
}

// Agent keep in sync: ParameterRowUpdater in
// benchling/notebook/structured_tables/resync/row_updaters/parameter.py
export const ParameterStructuredTableRowUpdater: StructuredTableRowUpdater<
  ParameterVersionLiveData,
  ParameterStructuredTableRowMetadata
> = {
  updateRow(
    model: ParameterVersionLiveData | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ParameterStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ParameterStructuredTableRowMetadata> {
    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];

      if (
        columnGroup === StructuredTableColumnGroups.REFERENCE &&
        column.columnGroup === StructuredTableColumnGroups.REFERENCE &&
        column.isReadOnly
      ) {
        const lcv = this.updateCell(model, columnGroup, column);
        return updateCellWithLiveValue(cell, lcv);
      }

      if (!column.isReadOnly) {
        return getUpdatedCellWithLSUV(column.provenanceUuid, cell, row.lastSyncedUpstreamValues);
      } else {
        return cell;
      }
    });

    return {
      ...row,
      cells,
    };
  },

  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<ParameterVersionLiveData | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<ParameterVersionLiveData | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache(sampleLookupInfo: SampleLookupInfo): ParameterVersionLiveData | null {
    return readFragmentFromCache(PARAMETER_VERSION_LIVE_DATA_FRAGMENT_WRAPPER, sampleLookupInfo.idForLookup);
  },

  updateCell(
    model: ParameterVersionLiveData | InaccessibleInfoGraphQL,
    _columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }

    switch (column.provenanceUuid) {
      case ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID:
        return toStringTypedValue(model.name);
      case ParameterStructuredTableColumnUuid.DESCRIPTION_COLUMN_UUID:
        return toStringTypedValue(model.description);
      default:
        return 'NO_VALUE';
    }
  },
};
